import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';

import AppLayout from '@/components/AppLayout';
import PageSkeleton from '@/components/PageSkeleton';

import { legacyPaths, membershipPath, paths } from './paths';
import RequirePermission from './RequirePermission';

/**
 * Mapa de rotas da aplicação.
 *
 * Páginas entram por `lazy` para não crescerem o bundle inicial — o Vite gera um chunk
 * por página. O `<Suspense>` fica dentro do layout, para a moldura continuar em tela
 * enquanto o chunk carrega, com o esqueleto no lugar do conteúdo.
 *
 * Os caminhos vêm de `paths.ts`, o mesmo módulo que os links usam.
 */
const Dashboard = lazy(() => import('@/pages/Dashboard'));
const Agenda = lazy(() => import('@/pages/Dashboard/Agenda'));
const Schedules = lazy(() => import('@/pages/Schedules'));
const Generate = lazy(() => import('@/pages/Generate'));
const Membership = lazy(() => import('@/pages/Membership'));
const Events = lazy(() => import('@/pages/Events'));
const Ministries = lazy(() => import('@/pages/Ministries'));
const Members = lazy(() => import('@/pages/Members'));
const MemberProfile = lazy(() => import('@/pages/MemberProfile'));
const Notifications = lazy(() => import('@/pages/Notifications'));
const Settings = lazy(() => import('@/pages/Settings'));
const Accesses = lazy(() => import('@/pages/Accesses'));
const Organizations = lazy(() => import('@/pages/Organizations'));
const NotFound = lazy(() => import('@/pages/NotFound'));

/** O endereço antigo leva ao novo com a mesma consulta (`/escalas?visao=semana`). */
function Redirect({ to }: { to: string }) {
  const { search } = useLocation();

  return <Navigate to={`${to}${search}`} replace />;
}

export default function AppRoutes() {
  return (
    <Routes>
      <Route element={<AppLayout />}>
        <Route
          element={
            <Suspense fallback={<PageSkeleton />}>
              <Outlet />
            </Suspense>
          }
        >
          <Route path={paths.home} element={<Dashboard />} />
          <Route path={paths.agenda} element={<Agenda />} />
          <Route path={paths.schedules} element={<Schedules />} />
          <Route
            path={paths.generate}
            element={
              <RequirePermission allow={(p) => p.generate}>
                <Generate />
              </RequirePermission>
            }
          />
          <Route
            path={paths.membership}
            element={
              <RequirePermission allow={(p) => p.viewMembership}>
                <Membership />
              </RequirePermission>
            }
          />
          <Route path={legacyPaths.schedules} element={<Redirect to={paths.schedules} />} />
          <Route path={legacyPaths.generate} element={<Redirect to={paths.generate} />} />
          <Route
            path={legacyPaths.distribution}
            element={<Navigate to={membershipPath('servico')} replace />}
          />
          <Route
            path={paths.events}
            element={
              <RequirePermission allow={(p) => p.isManager}>
                <Events />
              </RequirePermission>
            }
          />
          {[paths.ministries, paths.ministry].map((path) => (
            <Route
              key={path}
              path={path}
              element={
                <RequirePermission allow={(p) => p.isManager}>
                  <Ministries />
                </RequirePermission>
              }
            />
          ))}
          <Route
            path={paths.members}
            element={
              <RequirePermission allow={(p) => p.isManager}>
                <Members />
              </RequirePermission>
            }
          />
          <Route
            path={paths.newMember}
            element={
              <RequirePermission allow={(p) => p.manageMembers}>
                <MemberProfile />
              </RequirePermission>
            }
          />
          <Route path={paths.member} element={<MemberProfile />} />
          <Route path={paths.me} element={<MemberProfile />} />
          <Route path={paths.notifications} element={<Notifications />} />
          <Route
            path={paths.accesses}
            element={
              <RequirePermission allow={(p) => p.manageAccess}>
                <Accesses />
              </RequirePermission>
            }
          />
          <Route
            path={paths.orgs}
            element={
              <RequirePermission allow={(p) => p.manageOrgs}>
                <Organizations />
              </RequirePermission>
            }
          />
          <Route
            path={paths.settings}
            element={
              <RequirePermission allow={(p) => p.manageSettings}>
                <Settings />
              </RequirePermission>
            }
          />
          <Route path="*" element={<NotFound />} />
        </Route>
      </Route>
    </Routes>
  );
}
